import { Avatar as BaseAvatar } from "@base-ui/react/avatar";
import { cx } from "../../lib/cx";

/**
 * The person's monogram circle, on Base UI's Avatar. A2's `avatar.tsx`.
 *
 * The initial is the first letter of `name`; a `src` photo, when there is
 * one, replaces it once it has loaded (Base UI keeps the initial up until
 * then, and after a failed load). `dot` is A2's small ember mark for a
 * profile with unanswered questions; the caller decides when it shows,
 * because the completeness rule is the profile's, not the avatar's.
 * A2's `ring` mode (the completeness meter around the circle) is the profile
 * screen's and is not here.
 *
 * It is decoration: the name it abbreviates is always said by something else
 * (the greeting, or the label of the button it sits in), so it has no name
 * of its own and the photo's `alt` is empty.
 */

export type AvatarSize = "sm" | "md" | "lg";

const SIZE: Record<AvatarSize, string> = {
  sm: "size-9 text-base",
  md: "size-11 text-lg",
  lg: "size-16 text-2xl",
};

const DOT: Record<AvatarSize, string> = {
  sm: "size-2.5",
  md: "size-3",
  lg: "size-4",
};

export interface AvatarProps {
  className?: string;
  /** Show the ember "something is missing" dot. */
  dot?: boolean;
  name: string;
  size?: AvatarSize;
  src?: string;
}

export function initialOf(name: string): string {
  const first = name.trim().charAt(0);
  return first ? first.toLocaleUpperCase() : "?";
}

export function Avatar({ name, src, size, dot, className }: AvatarProps) {
  return (
    <span className={cx("relative inline-grid shrink-0", className)}>
      <BaseAvatar.Root
        className={cx(
          "a-display grid select-none place-items-center overflow-hidden rounded-full bg-inverse text-on-inverse leading-none",
          SIZE[size ?? "md"]
        )}
      >
        {src ? (
          <BaseAvatar.Image
            alt=""
            className="size-full object-cover"
            src={src}
          />
        ) : null}
        <BaseAvatar.Fallback aria-hidden="true">
          {initialOf(name)}
        </BaseAvatar.Fallback>
      </BaseAvatar.Root>
      {dot ? (
        <span
          aria-hidden="true"
          className={cx(
            "absolute top-0 right-0 rounded-full bg-accent ring-2 ring-canvas",
            DOT[size ?? "md"]
          )}
          data-avatar-dot
        />
      ) : null}
    </span>
  );
}
